// Type definitions
interface EncryptedData {
  iv: string;
  cipher: string;
}

const encode = (str: string): Uint8Array => new TextEncoder().encode(str);
const decode = (buf: ArrayBuffer): string => new TextDecoder().decode(buf);
const toArrayBuffer = (view: Uint8Array): ArrayBuffer => view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;

// Convert a raw key (e.g., a byte array or string) into a CryptoKey
export async function importKey(rawKey: string): Promise<CryptoKey> {
  const keyBuffer: Uint8Array = new TextEncoder().encode(rawKey); // Convert the string to a Uint8Array
  return await crypto.subtle.importKey(
    "raw", // Key format
    toArrayBuffer(keyBuffer), // Raw key data
    { name: "AES-GCM" }, // Algorithm
    true, // Whether the key is extractable
    ["encrypt", "decrypt"] // Key usages
  );
}

// Encrypt plaintext string using the key
export async function AesEncrypt(key: CryptoKey, data: string | object): Promise<EncryptedData> {

  const iv: Uint8Array = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
  const ivBuffer = toArrayBuffer(iv);

  // Check if the input is an object, and stringify it if necessary
  const plainText: string = typeof data === "object" ? JSON.stringify(data) : data;
  const encoded: Uint8Array = encode(plainText);

  const encrypted: ArrayBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: ivBuffer },
    key,
    toArrayBuffer(encoded)
  );

// Helper to convert Uint8Array to Base64 without using spread operator
  const uint8ArrayToBase64 = (arr: Uint8Array): string => {
    let binary = '';
    for (let i = 0; i < arr.length; i++) {
      binary += String.fromCharCode(arr[i]);
    }
    return btoa(binary);
  };

  return {
    iv: uint8ArrayToBase64(iv), // Base64 IV
    cipher: uint8ArrayToBase64(new Uint8Array(encrypted)) // Base64 Cipher
  };
}

// Decrypt ciphertext using the key
export async function AesDecrypt(key: CryptoKey, encryptedData: EncryptedData): Promise<any> {
  if (!encryptedData || typeof encryptedData !== 'object' || typeof encryptedData.iv !== 'string' || typeof encryptedData.cipher !== 'string') {
    return encryptedData;
  }

  try {
    const iv: Uint8Array = new Uint8Array(atob(encryptedData.iv).split('').map(c => c.charCodeAt(0)));
    const data: Uint8Array = new Uint8Array(atob(encryptedData.cipher).split('').map(c => c.charCodeAt(0)));
    const ivBuffer = toArrayBuffer(iv);

    const decrypted: ArrayBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: ivBuffer },
      key,
      toArrayBuffer(data)
    );

    const decoded: string = decode(decrypted);

    try {
      return JSON.parse(decoded);
    } catch (e) {
      return decoded;
    }
  } catch (e) {
    return encryptedData;
  }
}

// Example usage with a predefined key
export const keyPromise: Promise<CryptoKey> = importKey(process.env.REACT_APP_ENCRYPTION_KEY as string); // Import the key from .env